import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { ReplaySubject, Subject, firstValueFrom, of } from 'rxjs';
import { takeUntil, take, catchError, finalize } from 'rxjs/operators';
import { BusinessPartnerDto } from 'src/app/domain/models/dto/business-partner.dto';
import { ResponseObjectModel } from 'src/app/domain/models/response-object.model';
import { CorporateAccountService } from 'src/app/shared/services/corporate-account.service';

@Component({
  selector: 'app-partner-document',
  templateUrl: './partner-document.component.html',
  styleUrls: ['./partner-document.component.scss'],
})
export class PartnerDocumentComponent implements OnInit, OnDestroy {
  partnerDocumentForm: FormGroup;
  loading: boolean = false;

  public partnerFilterCtrl = new FormControl('');
  public filteredPartners: ReplaySubject<BusinessPartnerDto[]> =
    new ReplaySubject<BusinessPartnerDto[]>(1);
  partners: BusinessPartnerDto[] = [];
  @ViewChild('partnerSelect', { static: true }) partnerSelect!: MatSelect;

  @ViewChild('singleSelect', { static: true }) singleSelect!: MatSelect;

  @ViewChild('fileInput', { static: true })
  fileInput!: ElementRef<HTMLInputElement>;

  protected _onDestroy = new Subject<void>();

  selectedFileName = '';
  responseMsg = '';

  constructor(
    private formBuilder: FormBuilder,
    private companyService: CorporateAccountService
  ) {
    this.partnerDocumentForm = this.formBuilder.group({
      partnerId: ['', Validators.required],
      file: [null, Validators.required],
    });
  }

  get accountFormControl() {
    return this.partnerDocumentForm.controls as any;
  }

  async ngOnInit() {
    const partnersRes = await firstValueFrom(
      this.companyService
        .getPartnersList()
        .pipe(
          catchError(() =>
            of({ data: [] } as ResponseObjectModel<BusinessPartnerDto[]>)
          )
        )
    );
    this.partners = partnersRes.data ?? [];
    this.filteredPartners.next(this.partners);

    this.partnerFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => this.filterPartners());

    this.setInitialValue();
  }

  changePartner(event: any) {
    this.partnerDocumentForm.controls['partnerId'].setValue(event.value);
  }

  protected setInitialValue() {
    this.filteredPartners
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        this.partnerSelect.compareWith = (
          a: string | number,
          b: string | number
        ) => a != null && b != null && String(a) === String(b);
      });
  }

  protected filterPartners() {
    if (!this.partners) return;

    const raw = this.partnerFilterCtrl.value ?? '';
    const search = this.normalize(String(raw));

    if (!search) {
      this.filteredPartners.next(this.partners);
      return;
    }

    this.filteredPartners.next(
      this.partners.filter((p) => {
        const name = this.normalize(p?.companyName ?? '');
        const agency = String(p?.agencyNumber ?? '').toLowerCase();

        return name.includes(search) || agency.includes(search);
      })
    );
  }

  private normalize(text: string): string {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  onFileSelected(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (file) {
      this.selectedFileName = file.name;

      this.partnerDocumentForm.controls['file'].setValue(file);
      this.partnerDocumentForm.controls['file'].updateValueAndValidity({
        emitEvent: true,
      });

      this.fileInput.nativeElement.value = '';
    } else {
      this.selectedFileName = '';
      this.partnerDocumentForm.controls['file'].reset();
    }
  }

  onClear() {
    this.partnerDocumentForm.reset();
    this.selectedFileName = '';
    this.filteredPartners.next(this.partners);
    this.partnerFilterCtrl.setValue('');
    this.responseMsg = '';
    this.fileInput.nativeElement.value = '';
  }

  onSubmit() {
    if (this.partnerDocumentForm.invalid) {
      this.partnerDocumentForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    const { partnerId, file } = this.partnerDocumentForm.value as {
      partnerId: number | string;
      file: File;
    };

    this.companyService
      .uploadPartnerFile({
        partnerId,
        file,
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.responseMsg =
            typeof res === 'object' ? JSON.stringify(res, null, 2) : res;
        },
        error: (err) => {
          this.responseMsg =
            typeof err === 'object' ? JSON.stringify(err, null, 2) : err;
        },
      });
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
